// Sibling to pattern-svg.util.ts, but generates a self-contained tiled SVG procedurally instead of
// recoloring a fetched catalog file - the browser-side port of project-service's
// GeometricPatternSvgUtil (Java), which MUST stay byte-for-byte equivalent so an AI-placed
// geometric pattern renders identically to a manually-placed one (see
// docs/plans/design3d-geometric-pattern-plan.md's "Backend integration" section).
//
// Unlike a catalog pattern (fetched at a fixed native size, then uniformly scaled to cover its
// target - see addPattern()/rebuildPatternImage()), this generates the SVG at exactly the
// caller's target pixel size every time - the repeat grid is baked in at generation time, so the
// caller places the result the same simple way it already places any other image, no separate
// native-size/uniform-scale/center step needed.

export type GeometricPatternStyle = 'STRIPES' | 'CHECKERBOARD' | 'DOTS' | 'DIAGONAL_LINES' | 'GRID';

const MIN_TILE_SIZE_PX = 8;
const DEFAULT_ACCENT_COLOR = '#000000';

/**
 * @param style one of STRIPES/CHECKERBOARD/DOTS/DIAGONAL_LINES/GRID (case-insensitive; anything
 *   else falls back to STRIPES)
 * @param backgroundColor the tile's base fill
 * @param accentColors the shape color(s) drawn over the background - only the first is used,
 *   falling back to DEFAULT_ACCENT_COLOR if empty/blank
 * @param tileScale fraction (0..1) of min(width, height) each repeat tile spans - smaller means
 *   more, thinner repeats; clamped to a sane minimum so a near-zero value doesn't generate a
 *   degenerate/invisible tile
 * @param angleDegrees rotates the whole tile grid, matching Fabric's own clockwise-positive angle
 *   convention
 * @param width the exact SVG width to generate, in pixels
 * @param height the exact SVG height to generate, in pixels
 */
export function svgPatternTile(
  style: string,
  backgroundColor: string,
  accentColors: string[],
  tileScale: number,
  angleDegrees: number,
  width: number,
  height: number,
): string {
  const accent = accentColors?.[0]?.trim() ? accentColors[0] : DEFAULT_ACCENT_COLOR;
  const tileSize = Math.max(MIN_TILE_SIZE_PX, tileScale * Math.min(width, height));
  const tileContent = tileContentFor(style, accent, tileSize);
  // DIAGONAL_LINES' own tile content is a plain vertical line (see tileContentFor()) rather than a
  // literal corner-to-corner diagonal - this bakes the 45° in here instead, on top of whatever
  // angleDegrees the caller passed. See tileContentFor()'s own comment for why.
  const styleAngleOffset = (style ?? '').toUpperCase() === 'DIAGONAL_LINES' ? 45 : 0;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
    `<defs><pattern id="tile" width="${tileSize}" height="${tileSize}" patternUnits="userSpaceOnUse" ` +
    `patternTransform="rotate(${angleDegrees + styleAngleOffset})">${tileContent}</pattern></defs>` +
    `<rect width="100%" height="100%" fill="${backgroundColor}"/>` +
    `<rect width="100%" height="100%" fill="url(#tile)"/>` +
    `</svg>`
  );
}

function tileContentFor(style: string, accent: string, tileSize: number): string {
  switch ((style ?? '').toUpperCase()) {
    case 'CHECKERBOARD': {
      const half = tileSize / 2;
      return (
        `<rect x="0" y="0" width="${half}" height="${half}" fill="${accent}"/>` +
        `<rect x="${half}" y="${half}" width="${half}" height="${half}" fill="${accent}"/>`
      );
    }
    case 'DOTS': {
      const radius = tileSize / 4;
      const center = tileSize / 2;
      return `<circle cx="${center}" cy="${center}" r="${radius}" fill="${accent}"/>`;
    }
    case 'DIAGONAL_LINES': {
      // A literal corner-to-corner diagonal (the previous approach here) touches the tile edge at
      // a shallow, non-perpendicular angle exactly at each corner - browsers rasterize repeating
      // <pattern> content as a cached tile bitmap, and a diagonal line's antialiased edge lands on
      // very slightly different sub-pixels each time that bitmap gets re-tiled, so the "same" line
      // resumes a pixel or so off from where the previous tile left it: a visible kink at every
      // tile boundary. A plain axis-aligned line spanning the tile's own edge doesn't have that
      // problem (see GRID below, which never showed this) - drawing this vertically and letting
      // svgPatternTile()'s own patternTransform rotate the whole tile grid 45° (baked in there,
      // alongside the caller's own angleDegrees) reproduces the same 45° look and the same T/√2
      // perpendicular line spacing, entirely avoiding the per-tile diagonal seam.
      const strokeWidth = Math.max(1, tileSize / 10);
      return `<line x1="0" y1="0" x2="0" y2="${tileSize}" stroke="${accent}" stroke-width="${strokeWidth}"/>`;
    }
    case 'GRID': {
      const strokeWidth = Math.max(1, tileSize / 12);
      return (
        `<line x1="0" y1="0" x2="0" y2="${tileSize}" stroke="${accent}" stroke-width="${strokeWidth}"/>` +
        `<line x1="0" y1="0" x2="${tileSize}" y2="0" stroke="${accent}" stroke-width="${strokeWidth}"/>`
      );
    }
    case 'STRIPES':
    default: {
      const half = tileSize / 2;
      return `<rect x="0" y="0" width="${tileSize}" height="${half}" fill="${accent}"/>`;
    }
  }
}
